import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  CheckCircle2,
  ChevronLeft,
  Video,
  Mic,
  MessageSquare,
  Clock,
  Lightbulb
} from 'lucide-react';

// Translations for the UI
const translations = {
  en: {
    pageTitle: "You're All Set!",
    readyToStart: 'Ready to start your interview with',
    setupConfirmed: "Your camera and microphone are working perfectly. You're ready to begin!",
    questionsToAnswer: 'Questions to Answer',
    questionsCovering: 'questions covering your expertise',
    estimatedTime: 'Estimated Time',
    timeEstimate: '20-30 minutes (you can pause anytime)',
    recordingFormat: 'Recording Format',
    recordingDescription: 'Audio and video will be recorded locally in your browser',
    quickTips: 'Quick Tips for a Great Interview',
    tip1: 'Speak naturally and take your time with answers',
    tip2: 'Look at the camera when speaking',
    tip3: 'Use the pause button if you need a moment to think',
    tip4: "Don't worry about being perfect - authenticity matters more",
    tip5: 'You can review your answers before submitting',
    privacyTitle: 'Privacy & Security',
    privacyDescription: "Your recording is processed securely and will only be used to create the interview article. You'll have the opportunity to review the final content before it's published.",
    backToSetup: 'Back to Setup',
    startRecording: 'Start Recording',
    finalCheck: "Make sure you're in a quiet space with good lighting before starting"
  },
  es: {
    pageTitle: '¡Todo Listo!',
    readyToStart: 'Listo para comenzar su entrevista con',
    setupConfirmed: 'Su cámara y micrófono están funcionando perfectamente. ¡Está listo para comenzar!',
    questionsToAnswer: 'Preguntas a Responder',
    questionsCovering: 'preguntas sobre su experiencia',
    estimatedTime: 'Tiempo Estimado',
    timeEstimate: '20-30 minutos (puede pausar en cualquier momento)',
    recordingFormat: 'Formato de Grabación',
    recordingDescription: 'Audio y video serán grabados localmente en su navegador',
    quickTips: 'Consejos para una Gran Entrevista',
    tip1: 'Hable de forma natural y tómese su tiempo con las respuestas',
    tip2: 'Mire a la cámara cuando hable',
    tip3: 'Use el botón de pausa si necesita un momento para pensar',
    tip4: 'No se preocupe por ser perfecto - la autenticidad es más importante',
    tip5: 'Puede revisar sus respuestas antes de enviarlas',
    privacyTitle: 'Privacidad y Seguridad',
    privacyDescription: 'Su grabación se procesa de forma segura y solo se utilizará para crear el artículo de la entrevista. Tendrá la oportunidad de revisar el contenido final antes de su publicación.',
    backToSetup: 'Volver a Configuración',
    startRecording: 'Comenzar Grabación',
    finalCheck: 'Asegúrese de estar en un espacio tranquilo con buena iluminación antes de comenzar'
  },
  ca: {
    pageTitle: 'Tot Llest!',
    readyToStart: 'Llest per començar la vostra entrevista amb',
    setupConfirmed: 'La vostra càmera i micròfon funcionen perfectament. Esteu llest per començar!',
    questionsToAnswer: 'Preguntes a Respondre',
    questionsCovering: 'preguntes sobre la vostra experiència',
    estimatedTime: 'Temps Estimat',
    timeEstimate: '20-30 minuts (podeu pausar en qualsevol moment)',
    recordingFormat: 'Format de Gravació',
    recordingDescription: "L'àudio i el vídeo es gravaran localment al vostre navegador",
    quickTips: 'Consells per a una Gran Entrevista',
    tip1: 'Parleu de forma natural i preneu-vos el vostre temps amb les respostes',
    tip2: 'Mireu a la càmera quan parleu',
    tip3: 'Utilitzeu el botó de pausa si necessiteu un moment per pensar',
    tip4: "No us preocupeu per ser perfecte - l'autenticitat és més important",
    tip5: 'Podeu revisar les vostres respostes abans d\'enviar-les',
    privacyTitle: 'Privacitat i Seguretat',
    privacyDescription: "La vostra gravació es processa de forma segura i només s'utilitzarà per crear l'article de l'entrevista. Tindreu l'oportunitat de revisar el contingut final abans de la seva publicació.",
    backToSetup: 'Tornar a Configuració',
    startRecording: 'Començar Gravació',
    finalCheck: "Assegureu-vos d'estar en un espai tranquil amb bona il·luminació abans de començar"
  }
};

type Language = keyof typeof translations;

interface ExpertReadyScreenProps {
  interview: {
    expertName: string;
    language?: string;
    journalist: {
      name: string;
    };
    questions: Array<{
      id: string;
      text: string;
    }>;
  };
  onStart: () => void;
  onBack: () => void;
}

export function ExpertReadyScreen({ interview, onStart, onBack }: ExpertReadyScreenProps) {
  // Get translations for the interview language
  const lang = (interview.language || 'en') as Language;
  const t = translations[lang] || translations.en;

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-3xl">
        <CardHeader className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
          <CardTitle className="text-3xl font-bold mb-2">
            {t.pageTitle}
          </CardTitle>
          <p className="text-lg text-muted-foreground">
            {t.readyToStart} {interview.journalist.name}
          </p>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Setup Confirmed */}
          <Alert className="border-green-200 bg-green-50">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-900">
              {t.setupConfirmed}
            </AlertDescription>
          </Alert>

          {/* Interview Info */}
          <div className="grid gap-4">
            <div className="flex items-start gap-3 p-4 bg-muted rounded-lg">
              <MessageSquare className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium mb-1">{t.questionsToAnswer}</p>
                <p className="text-sm text-muted-foreground">
                  {interview.questions.length} {t.questionsCovering}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-muted rounded-lg">
              <Clock className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium mb-1">{t.estimatedTime}</p>
                <p className="text-sm text-muted-foreground">
                  {t.timeEstimate}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-muted rounded-lg">
              <Video className="w-5 h-5 text-primary mt-0.5" />
              <div>
                <p className="font-medium mb-1">{t.recordingFormat}</p>
                <p className="text-sm text-muted-foreground">
                  {t.recordingDescription}
                </p>
              </div>
            </div>
          </div>

          {/* Tips */}
          <div className="space-y-3">
            <h3 className="font-semibold flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-yellow-500" />
              {t.quickTips}
            </h3>

            <div className="space-y-2 text-sm">
              <div className="flex gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <p>{t.tip1}</p>
              </div>
              <div className="flex gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <p>{t.tip2}</p>
              </div>
              <div className="flex gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <p>{t.tip3}</p>
              </div>
              <div className="flex gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <p>{t.tip4}</p>
              </div>
              <div className="flex gap-2">
                <span className="text-green-600 font-bold">✓</span>
                <p>{t.tip5}</p>
              </div>
            </div>
          </div>

          {/* Privacy Note */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm">
            <p className="font-medium mb-2">{t.privacyTitle}</p>
            <p className="text-muted-foreground">
              {t.privacyDescription}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between pt-4">
            <Button
              variant="outline"
              onClick={onBack}
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              {t.backToSetup}
            </Button>

            <Button
              onClick={onStart}
              size="lg"
              className="px-8 bg-green-600 hover:bg-green-700"
            >
              <Video className="w-4 h-4 mr-2" />
              {t.startRecording}
            </Button>
          </div>

          {/* Final Check Reminder */}
          <div className="text-center text-sm text-muted-foreground pt-4 border-t">
            <p>{t.finalCheck}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
